import React, { useEffect, useState } from 'react';
import Navbar from '../common/Navbar/Navbar';
import Footer from '../footer/Footer';
import VideoSlider from '../common/VideoSlider/VideoSlider';
import { toAbsoluteUrl } from '../../assets/helpers/AssetHelpers';
import { CmsVideo } from '../../types/types';
import VideoHeader from './VideoHeader';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { getVideo } from '../../services/api/Api';
import SkeletonComponent from '../SkeletonComponent';
import SubNavbar from '../common/Navbar/SubNavbar';

const VideoPage = () => {
	const [sliderVideoData, setSliderVideoData] = useState([]);
	const [allowedCategories, setAllowedCategories] = useState<{ name: string; new_id: string }[]>(
		[],
	);
	const [expandedStates, setExpandedStates] = useState<boolean[]>([]);
	const navigate = useNavigate();

	const baseUrl = window.location.origin;
	const [VideoLoading, setVideoLoading] = useState(false);

	useEffect(() => {
		const fetchVideoData = async () => {
			setVideoLoading(true);

			try {
				const response = await getVideo();
				if (response?.message === 'Success') {
					const allCategories = response?.data;

					const sortedCategories = allCategories
						.filter(
							(category: { parent_cms_category: string; rank: number }) =>
								category.parent_cms_category === 'Video' && category.rank !== 0,
						)
						.sort((a: { rank: number }, b: { rank: number }) => a.rank - b.rank);

					const dynamicCategories = sortedCategories.map(
						(category: { category_name: string; new_id: string }) => ({
							name: category.category_name,
							new_id: category.new_id,
						}),
					);

					setAllowedCategories(dynamicCategories);

					const filteredVideos = allCategories.reduce(
						(
							acc: any[],
							category: { category_name: string; cms_videos: any[]; video_category: string },
						) => {
							if (
								dynamicCategories.some(
									(cat: { name: string }) => cat.name === category.category_name,
								) &&
								category.cms_videos &&
								category.cms_videos.length > 0
							) {
								acc.push(
									...category.cms_videos.map((video: CmsVideo) => {
										const videoTags = video.element_note.split(' ');
										const srcTag =
											videoTags.find((tag: string | string[]) => tag.includes('src')) || '';
										const videoUrlTagsArray = srcTag.split('/');
										const videoId = videoUrlTagsArray[videoUrlTagsArray.length - 1].split('?')[0];

										return {
											id: videoId,
											element_note: video.element_note,
											parent: video.parent,
											title: video.video_title,
											category: video.video_category,
											video_parent_category: category.category_name,
											video_faq_id: video.faq_mapping_with_video_title,
											faq_category_hierarchy: video.faq_category_hierarchy,
										};
									}),
								);
							}
							return acc;
						},
						[],
					);
					setSliderVideoData(filteredVideos);
					setExpandedStates(new Array(dynamicCategories.length).fill(false));
				}
			} catch (error) {
				console.error('Failed to fetch video data:', error);
			} finally {
				setVideoLoading(false);
			}
		};

		fetchVideoData();
	}, []);

	const toggleExpand = (index: number) => {
		setExpandedStates((prevStates) => prevStates.map((state, i) => (i === index ? !state : state)));
	};

	const getVideosByCategory = (category: string) => {
		return sliderVideoData.filter((video: any) => video.video_parent_category === category);
	};

	return (
		<>
			<Navbar />
      		<SubNavbar />
			<section id="videoSection">
				<div className="container">
					<div className="row">
						<div className="col py-4">
							<div className="row">
								<div className="col-lg-11 mx-auto px-4 px-md-5 px-lg-2">
									<VideoHeader />
									{VideoLoading ? (
										<SkeletonComponent name="VideoSection" />
									) : (
										allowedCategories.map((category, index) => {
											const videos = getVideosByCategory(category.name);

											if (videos.length > 0) {
												const categoryUrl = `${baseUrl}/videos/${category.new_id}`;
												const emailSubject = encodeURIComponent('Check out this video collection!');
												const emailBody = encodeURIComponent(
													`I thought you might find this interesting: ${categoryUrl}`,
												);
												return (
													<div key={index} className={index === 0 ? 'mt-4' : 'mt-5'}>
														<div className="d-sm-flex justify-content-between align-items-center mb-3">
															<div
																role="button"
																className="ur_semi color1 fs-28"
																onClick={() => {
																	window.scrollTo(0, 0);
																	navigate(`/videos/${category.new_id}`, { state: { videos } });
																}}>
																{category.name}
															</div>
															<div className="d-flex gap-2 align-items-center">
																<a
																	href={`https://wa.me/?text=${categoryUrl}`}
																	target="_blank"
																	rel="noopener noreferrer"
																	className="btn btn-sm btn-light bg-transparent border-0">
																	<img
																		src={toAbsoluteUrl('video-section/Whatsapp.svg')}
																		alt="WhatsApp"
																		width={24}
																	/>
																</a>

																<a
																	href={`mailto:?subject=${emailSubject}&body=${emailBody}`}
																	className="btn btn-sm btn-light bg-transparent border-0">
																	<img
																		src={toAbsoluteUrl('video-section/gmail.svg')}
																		alt="Gmail"
																		width={24}
																	/>
																</a>

																<button
																	type="button"
																	className="btn bg-transparent ps-1 border-0"
																	onClick={() => {
																		navigator.clipboard.writeText(categoryUrl);
																		toast.success('Link copied to clipboard');
																	}}>
																	<img
																		src={toAbsoluteUrl('video-section/share.svg')}
																		alt="Copy"
																		width={24}
																	/>
																</button>
															</div>
														</div>
														<div>
															<VideoSlider
																videoData={videos}
																buttonUrl="#"
																isExpanded={expandedStates[index]}
																onClick={() => toggleExpand(index)}
																categoryId={category.name}
															/>
														</div>
													</div>
												);
											}
											return null;
										})
									)}
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>
			<Footer />
		</>
	);
};

export default VideoPage;
